import React, {SyntheticEvent, useContext, useEffect, useState} from "react";
import {Box, Stack, Tab, Tabs} from "@mui/material";
import {useTranslation} from "react-i18next";
import {FormProvider, useForm} from "react-hook-form";
import { useParams} from "react-router-dom";
import {useQuery} from "@tanstack/react-query";
import {AxiosError} from "axios";
import {useRecoilState, useSetRecoilState} from "recoil";
import SectionHeader from "../../../util/component/SectionHeader";
import ContentBody from "../common/content-body/ContentBody";
import PracticeDetail from "./practice-detail/PracticeDetail";
import AttachedInformation from "./attached-information/AttachedInformation";
import ExpiredThresholds from "./expired-thresholds/ExpiredThresholds";
import {IExpiredPracticeDetailForm} from "../models/models";
import {EXPIRED_PRACTICES_DETAIL_FORM_VALIDATION_SCHEMA, EXPIRED_PRACTICES_QUERY_KEY, EXPIRED_PRACTICE_DETAIL_INITIAL_VALUE} from "../config";
import {fetchExpiredPracticeActionHistoryById, fetchExpiredPricticeById, getExpiredFromRaCode} from "../services/http.services";
import {snackbarTypes} from "../../../util/state/globalVariables";
import {GlobalContext} from "../../../util/state/GlobalContextProvider";
import { practiceActionHistory, practiceDetail} from "../state";
import CustomLoader from "../../../util/component/CustomLoader";
import { yupResolver } from "@hookform/resolvers/yup";
import { setSnackbar } from "../../../util/state/globalAction";
import dayjs from "dayjs";
import { useGetAvaiableExpiredActionQueryObserver } from "../services/queryObserver";
import { useHasScope } from "../../../config/globalConfig";
interface ExpiredPracticeDetailProps {
    isInsertMode?: boolean
}

const ExpiredPracticeDetail = ({isInsertMode = false}: ExpiredPracticeDetailProps) => {
    const hasScope = useHasScope;
    const {t} = useTranslation(['expiredManagement']);
    const {dispatchGlobalData} = useContext(GlobalContext);
    const params = useParams();

    const [expiredDetailTab, setExpiredDetailTab] = useState(0);

    const [prDetail, setPracticeDetail] = useRecoilState(practiceDetail);
    const setActionHistory = useSetRecoilState(practiceActionHistory);

    const expiredPracticeDetailForm = useForm<IExpiredPracticeDetailForm>({
        defaultValues: EXPIRED_PRACTICE_DETAIL_INITIAL_VALUE,
        resolver: yupResolver(EXPIRED_PRACTICES_DETAIL_FORM_VALIDATION_SCHEMA)
    });

    const avaiableExpiredActionQueryObserver = useGetAvaiableExpiredActionQueryObserver();

    const viewSnackbarMessage = (message: string, type = snackbarTypes.error) => {
        dispatchGlobalData({
            type: setSnackbar,
            payload: {
                snackbar: {
                    message: t(message, t('common.message.error.tryLater')),
                    type: type,
                    show: true,
                    autoClose: true,
                }
            }
        });
    }

    const handleChangeExpiredDetailTab = (e: React.SyntheticEvent, activeTab: 0|1|2) => {
        e.stopPropagation();

        setExpiredDetailTab(activeTab);
    }

    const handleUploadPostAction = () => {
        setExpiredDetailTab(1);
    }

    const fetchExpiredPracticeByIdQuery = useQuery(
        [EXPIRED_PRACTICES_QUERY_KEY.FETCH_EXPIRED_PRACTICE, {id: params.id}],
        () => fetchExpiredPricticeById(params.id),
        {
            enabled: false,
            onSuccess: (response: any) => {
                
                setPracticeDetail(response.data);
            },
            onError: (error: AxiosError) => {
                viewSnackbarMessage(`common.message.error.findById.${error?.response?.status}`);
            }
        }
    );

    const fetchExpiredPracticeActionHistoryByIdQuery = useQuery(
        [EXPIRED_PRACTICES_QUERY_KEY.FETCH_EXPIRED_PRACTICE_ACTION_HISTORY, {id: params.id}],
        () => fetchExpiredPracticeActionHistoryById(params.id),
        {
            enabled: false,
            onSuccess: (response: any) => {
                setActionHistory(response.data);
            },
            onError: (error: AxiosError) => {
                setActionHistory([]);
                viewSnackbarMessage('translation:error.tryLater');
            }
        }
    );

    const getExpiredFromRaCodeQuery = useQuery(
        [EXPIRED_PRACTICES_QUERY_KEY.GET_EXPIRED_FROM_RA_CODE, {id: params.id}],
        {
            enabled: false,
            queryFn: () => {
                if(!!params.id)
                    return getExpiredFromRaCode(params.id);
            },
            onSuccess(response) {

                setPracticeDetail(response.data);
                
            },
            onError(error: any) {

                viewSnackbarMessage(`common.message.error.getExpiredFromRaCode.${error?.response?.status}`);
                
            }
    });

    useEffect(() => {
        if(!!params?.id) {
            if(isInsertMode) {
                getExpiredFromRaCodeQuery.refetch();
            }
            else {
                fetchExpiredPracticeByIdQuery.refetch();
                fetchExpiredPracticeActionHistoryByIdQuery.refetch();
            }
        }
    }, [params?.id]);

    useEffect(() => {

        if(!prDetail) return;

        expiredPracticeDetailForm.setValue('detail.expiredCode', prDetail?.expiredCode || '');
        expiredPracticeDetailForm.setValue('detail.status', !!prDetail?.status ? prDetail?.status + (!!prDetail?.subStatus ? ' - ' + prDetail.subStatus : '') : '');
        expiredPracticeDetailForm.setValue('detail.watchlist', prDetail?.watchlist);
        expiredPracticeDetailForm.setValue('detail.rentalType', prDetail?.rentalType || '');
        expiredPracticeDetailForm.setValue('detail.createdDatetime', !!prDetail?.createdDatetime ? dayjs(prDetail?.createdDatetime) : null);

        expiredPracticeDetailForm.setValue('detail.competanceAmount', !!prDetail?.competanceAmount ? (prDetail?.competanceAmount/100).toFixed(2) : '');
        expiredPracticeDetailForm.setValue('detail.delayDays', prDetail?.delayDays);
        expiredPracticeDetailForm.setValue('detail.raCode', prDetail?.raCode || '');
        expiredPracticeDetailForm.setValue('detail.bookingCode', prDetail?.bookingCode || '');

        expiredPracticeDetailForm.setValue('detail.segment', prDetail?.segment || '');
        expiredPracticeDetailForm.setValue('detail.category', prDetail?.category || '');
        expiredPracticeDetailForm.setValue('detail.product',  prDetail?.product || '');
        expiredPracticeDetailForm.setValue('detail.rate', prDetail?.rate || '');

        expiredPracticeDetailForm.setValue('detail.billingAcriss', prDetail?.billingAcriss || '');
        expiredPracticeDetailForm.setValue('detail.effectiveAcriss', prDetail?.effectiveAcriss || '');
        expiredPracticeDetailForm.setValue('detail.vehicleModel', prDetail?.vehicleModel || '');
        expiredPracticeDetailForm.setValue('detail.plate', prDetail?.plate || '');

        expiredPracticeDetailForm.setValue('detail.effectiveCheckOutDate', !!prDetail?.effectiveCheckOutDate ? dayjs(prDetail?.effectiveCheckOutDate): null);
        expiredPracticeDetailForm.setValue('detail.effectiveCheckOutLocationLabel', prDetail?.effectiveCheckOutLocationLabel || '');
        expiredPracticeDetailForm.setValue('detail.expectedCheckInDate', !!prDetail?.expectedCheckInDate ? dayjs(prDetail?.expectedCheckInDate) : null);
        expiredPracticeDetailForm.setValue('detail.expectedeCheckInLocationLabel', prDetail?.expectedeCheckInLocationLabel || '');

        expiredPracticeDetailForm.setValue('detail.companyName', prDetail?.companyName || '');
        expiredPracticeDetailForm.setValue('detail.customerSapId', prDetail?.customerSapId || '');
        expiredPracticeDetailForm.setValue('detail.customerNameSurname', !prDetail?.companyName ? ((prDetail?.customerFirstName || '') + ' ' + (prDetail?.customerLastName || '')).trim() : '');
        expiredPracticeDetailForm.setValue('detail.customerEmail', prDetail?.customerEmail || '');
        expiredPracticeDetailForm.setValue('detail.customerMobile', prDetail?.customerMobile || '');

        expiredPracticeDetailForm.setValue('detail.recoveryCompany', prDetail?.recoveryCompany?.name || '');
        expiredPracticeDetailForm.setValue('detail.recoveryCompanyEmail', prDetail?.recoveryCompany?.email || '');

        expiredPracticeDetailForm.setValue('thresholds.firstSmsExpectedDate', !!prDetail?.actionDates?.firstSmsExpectedDate ? dayjs(prDetail?.actionDates?.firstSmsExpectedDate) : null);
        expiredPracticeDetailForm.setValue('thresholds.recoverySocietyExpectedDate', !!prDetail?.actionDates?.recoverySocietyExpectedDate ? dayjs(prDetail?.actionDates?.recoverySocietyExpectedDate) : null);
        expiredPracticeDetailForm.setValue('thresholds.formalNoticeExpectedDate', !!prDetail?.actionDates?.formalNoticeExpectedDate ? dayjs(prDetail?.actionDates?.formalNoticeExpectedDate) : null);
        expiredPracticeDetailForm.setValue('thresholds.lawSuitExpectedDate', !!prDetail?.actionDates?.lawSuitExpectedDate ? dayjs(prDetail?.actionDates?.lawSuitExpectedDate) : null);
        expiredPracticeDetailForm.setValue('thresholds.autoSms', prDetail?.actionDates?.autoSms);
        
    }, [prDetail]);

    //Trigger per status di Practice Detail
    useEffect(() => {
        if(!!prDetail?.status && !isInsertMode)
            avaiableExpiredActionQueryObserver.refetch();
    }, [prDetail?.status, isInsertMode]);

    return (
        <>
            <SectionHeader title={t('expiredDetail.title')} subtitle={isInsertMode ? t('expiredDetail.subtitleInsertMode') + (!!prDetail?.raCode ? ': ' + prDetail?.raCode : '') : t('expiredDetail.subtitleViewMode') + (!!prDetail?.expiredCode ? ': ' + prDetail?.expiredCode : '')}/>

            <ContentBody>
                <Stack direction="row" alignItems="center" columnGap={2} mb={2}>
                    <Tabs value={expiredDetailTab} onChange={handleChangeExpiredDetailTab} >
                        <Tab label={t('expiredDetail.tab.detail')} value={0} />
                        { !isInsertMode && <Tab label={t('expiredDetail.tab.attachments')} value={1} /> }
                        { hasScope("SCOPE_MANAGE_EXPIRED_THRESHOLD") && !isInsertMode && <Tab label={t('expiredDetail.tab.thresholds')}   value={2} /> }
                    </Tabs>
                </Stack>

                <Box role="tabpanel">
                    <FormProvider {...expiredPracticeDetailForm}>
                        {
                            expiredDetailTab === 0 &&
                            <PracticeDetail isInsertMode={isInsertMode} handleUploadPostAction={handleUploadPostAction}/>
                        }

                        {!isInsertMode && expiredDetailTab === 1 &&
                            <AttachedInformation />
                        }

                        {!isInsertMode && expiredDetailTab === 2 &&
                            <ExpiredThresholds />
                        }
                    </FormProvider>
                </Box>
                <CustomLoader loading={fetchExpiredPracticeByIdQuery.isFetching || fetchExpiredPracticeActionHistoryByIdQuery.isFetching || getExpiredFromRaCodeQuery.isFetching} />
            </ContentBody>
        </>
    )
}

export default ExpiredPracticeDetail;